import { Icon } from '../../../../components/icons/Icon'
import { InlineAlert } from '../../../../components/InlineAlert'
import { HelpBalloon } from '../../../../help/HelpBalloon'
import { FilterChipBar } from '../../filter/FilterChipBar'
import { WIDGET_KEY } from '../../widgets/types'
import { WidgetErrorBoundary } from '../../widgets/WidgetErrorBoundary'
import { PortfolioPipelineFunnelWidget } from '../../widgets/PortfolioPipelineFunnelWidget'
import { TotalCertifiedValueKpiWidget } from './TotalCertifiedValueKpiWidget'
import { StatusCountRowWidget } from './StatusCountRowWidget'
import { CertifiedValueTrendWidget } from './CertifiedValueTrendWidget'
import { TopJurisdictionsWidget } from './TopJurisdictionsWidget'
import { PortfolioClientBreakdownWidget } from './PortfolioClientBreakdownWidget'
import { DashboardThemePicker } from '../../../../theme/DashboardThemePicker'
import type { UsePortfolioAnalyticsResult } from './usePortfolioAnalytics'
import type { DashboardFilter } from '../../../../types/insights'
import '../../charts/charts.css'

const AXIS_LABELS: Record<string, string> = {
  fiscalYearId: 'Fiscal year',
  jurisdiction: 'Jurisdiction',
  accountId: 'Account',
  legalEntityId: 'Legal entity',
  kind: 'Kind',
  programId: 'Program',
  currentState: 'Status',
}

/**
 * IB-282 View 1 — `/insights/dashboards/portfolio`. Presentational grid of `WidgetFrame`-wrapped
 * tiles, each in its own `WidgetErrorBoundary` (double isolation — one tile's failure never blanks
 * the rest of the view, mirroring `JurisdictionAnalyticsPanel`'s/`DashboardScreen`'s own
 * composition exactly). Pure props-in, `usePortfolioAnalytics` owns all state.
 *
 * <p>AC1.7 scope guard: the RAID I-45-cut metrics and any deferred per-team capacity concept are
 * never added here — see `scopeGuard.test.ts` for the mechanical, testable assertion.
 */
export function PortfolioAnalyticsPanel(props: UsePortfolioAnalyticsResult) {
  const { state, dispatch, unknownKeys, noticeDismissed, dismissNotice, filterFieldsOpen, setFilterFieldsOpen, widgetProps } = props

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'auto' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--iw-space-3)',
          flexWrap: 'wrap',
          padding: 'var(--iw-space-4) var(--iw-space-5)',
          borderBottom: '1px solid var(--iw-border)',
          background: 'var(--iw-surface)',
        }}
      >
        <span style={{ color: 'var(--iw-accent)', display: 'inline-flex' }}>
          <Icon name="bar-chart" size={20} />
        </span>
        <h1 style={{ fontSize: 'var(--iw-size-lg)', margin: 0 }}>Portfolio &amp; executive overview</h1>
        <HelpBalloon id="list.insights" />
        <span style={{ flex: 1 }} />
        <button
          type="button"
          className="iw-btn iw-btn--sm"
          aria-expanded={filterFieldsOpen}
          onClick={() => setFilterFieldsOpen(!filterFieldsOpen)}
        >
          <Icon name="filter" size={14} />
          Filters
        </button>
        <DashboardThemePicker />
      </div>

      {filterFieldsOpen ? <PortfolioFilterFields state={state} dispatch={dispatch} /> : null}

      {unknownKeys.length > 0 && !noticeDismissed ? (
        <div style={{ padding: '0 var(--iw-space-5)', paddingTop: 'var(--iw-space-3)' }}>
          <InlineAlert
            severity="warning"
            action={
              <button type="button" className="iw-btn iw-btn--sm" onClick={dismissNotice}>
                Dismiss
              </button>
            }
          >
            Some filters in this link could not be applied and were ignored.
          </InlineAlert>
        </div>
      ) : null}

      <FilterChipBar state={state} dispatch={dispatch} axisLabels={AXIS_LABELS} />

      <div className="iw-dashboard-grid">
        {/* AC1.1 funnel — the shipped `buildPipelineStages` composition, promoted from a
            frontend-only widget to a first-class View-1 tile (logic unchanged). */}
        <WidgetErrorBoundary title="Portfolio pipeline by lifecycle stage" icon="activity">
          <PortfolioPipelineFunnelWidget {...widgetProps(WIDGET_KEY.portfolioPipelineFunnel)} />
        </WidgetErrorBoundary>

        {/* AC1.1 KPI — the corrected I-47 form: a separate, honest $ total (never a per-stage $ SUM). */}
        <WidgetErrorBoundary title="Total certified value" icon="dollar-sign">
          <TotalCertifiedValueKpiWidget {...widgetProps(WIDGET_KEY.creditValueByJurisdictionFy)} />
        </WidgetErrorBoundary>

        {/* AC1.2 */}
        <WidgetErrorBoundary title="Count by status" icon="briefcase">
          <StatusCountRowWidget {...widgetProps(WIDGET_KEY.filingsByStatus)} />
        </WidgetErrorBoundary>

        {/* AC1.3 — new `certified-value-by-fiscal-year` widget key. */}
        <WidgetErrorBoundary title="Certified value trend (YoY)" icon="activity">
          <CertifiedValueTrendWidget {...widgetProps(WIDGET_KEY.certifiedValueByFiscalYear)} />
        </WidgetErrorBoundary>

        {/* AC1.4 */}
        <WidgetErrorBoundary title="Top jurisdictions by certified value" icon="bar-chart">
          <TopJurisdictionsWidget {...widgetProps(WIDGET_KEY.creditValueByJurisdictionFy)} />
        </WidgetErrorBoundary>

        {/* AC1.6 — new `certified-value-by-account` widget key. Only offered on certified-value
            tiles (never the COUNT-based funnel/count-by-status tiles above, which carry no
            accountId dimension — never a dead click). */}
        <WidgetErrorBoundary title="Portfolio → client certified value" icon="bar-chart">
          <PortfolioClientBreakdownWidget {...widgetProps(WIDGET_KEY.certifiedValueByAccount)} />
        </WidgetErrorBoundary>
      </div>
    </div>
  )
}

interface PortfolioFilterFieldsProps {
  state: UsePortfolioAnalyticsResult['state']
  dispatch: UsePortfolioAnalyticsResult['dispatch']
}

/** Freeform refinement inputs — same disclosed "raw-ID input, no lookup picker yet" posture as `DashboardScreen`'s own `DashboardFilterFields`. */
function PortfolioFilterFields({ state, dispatch }: PortfolioFilterFieldsProps) {
  const fields: Array<{ axis: keyof typeof AXIS_LABELS; placeholder: string }> = [
    { axis: 'fiscalYearId', placeholder: 'Fiscal year ID' },
    { axis: 'jurisdiction', placeholder: 'Jurisdiction code (e.g. US-GA)' },
    { axis: 'accountId', placeholder: 'Account ID' },
    { axis: 'legalEntityId', placeholder: 'Legal entity ID' },
    { axis: 'programId', placeholder: 'Program ID' },
  ]
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 'var(--iw-space-3)',
        padding: 'var(--iw-space-3) var(--iw-space-5)',
        borderBottom: '1px solid var(--iw-border)',
      }}
    >
      {fields.map((f) => (
        <div className="iw-field" key={f.axis}>
          <label className="iw-field-label" htmlFor={`portfolio-analytics-filter-${f.axis}`}>
            {AXIS_LABELS[f.axis]}
          </label>
          <input
            id={`portfolio-analytics-filter-${f.axis}`}
            className="iw-input"
            defaultValue={state.filters[f.axis as keyof DashboardFilter] ?? ''}
            placeholder={f.placeholder}
            onBlur={(e) =>
              dispatch({ type: 'SET_FILTER', axis: f.axis as keyof DashboardFilter, value: e.target.value.trim() || undefined })
            }
          />
        </div>
      ))}
    </div>
  )
}
